import { useEffect, useState } from 'react'
import { api, errorText } from '../api/client'
import type { SendResult } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'

/** Compose a text or email to a lead-book contact.
 *
 *  The server decides whether anything goes out — consent, DNC and quiet hours are
 *  enforced there. This dialog only explains the decision before the officer
 *  writes a word, and offers to record SMS consent when the one missing thing is
 *  an opt-in they actually obtained. */
export function SendMessageModal({
  open,
  onClose,
  contactId,
  channel,
  context,
  onSent,
}: {
  open: boolean
  onClose: () => void
  contactId: number
  channel: 'sms' | 'email'
  /** Also show this touch on a loan's timeline. */
  context?: { loan_id?: string; person_key?: string }
  onSent?: (result: SendResult) => void
}) {
  const { data: contact, reload } = useAsync(
    () => (open ? api.contact(contactId) : Promise.resolve(null)),
    [open, contactId],
  )
  const { data: status } = useAsync(
    () => (open ? api.messagingStatus() : Promise.resolve(null)),
    [open],
  )
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (!open) {
      setSubject('')
      setBody('')
    }
  }, [open])

  // Opens with the message already written, the way the lead platform's composer
  // always did: this lead's own figures, in the officer's voice, with the sender
  // identity and the opt-out already in it. Anything typed before it arrives wins —
  // an officer who started writing should not have it replaced under them.
  const [drafting, setDrafting] = useState(false)
  useEffect(() => {
    if (!open) return
    let live = true
    setDrafting(true)
    api.messageDraft(contactId, channel)
      .then((d) => {
        if (!live) return
        setSubject((v) => v || d.subject)
        setBody((v) => v || d.body)
      })
      .catch(() => {})
      .finally(() => { if (live) setDrafting(false) })
    return () => { live = false }
  }, [open, contactId, channel])

  const simulated = status?.mode === 'simulated'
  const state = contact?.channels?.[channel]
  const missingOptIn = channel === 'sms' && state && !state.allowed && /opt-in/i.test(state.reason)
  const label = channel === 'sms' ? 'text' : 'email'
  const to = contact?.methods.find((m) =>
    channel === 'email' ? m.kind === 'email' : m.kind === 'mobile')?.value


  async function send() {
    setBusy(true)
    try {
      const result = channel === 'sms'
        ? await api.sendSms(contactId, body, context)
        : await api.sendEmail(contactId, subject, body, context)
      const messages: Record<SendResult['status'], [string, 'positive' | 'warning' | 'danger']> = {
        sent: [`${channel === 'sms' ? 'Text' : 'Email'} sent`, 'positive'],
        simulated: ['Logged — messaging is in simulated mode, nothing was sent', 'warning'],
        blocked: [`Not sent: ${result.reason}`, 'danger'],
        failed: [`Couldn't send: ${result.reason}`, 'danger'],
      }
      const [msg, tone] = messages[result.status]
      toast(msg, { tone })
      onSent?.(result)
      if (result.status === 'sent' || result.status === 'simulated') onClose()
      else reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const segments = Math.max(1, Math.ceil(body.length / 160))
  const canSend = Boolean(state?.allowed) && body.trim() && (channel === 'sms' || subject.trim())

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={channel === 'sms' ? 'Send a text' : 'Send an email'}
      subtitle={contact ? `To ${contact.full_name ?? 'this contact'}${to ? ` · ${to}` : ''}` : undefined}
      width={540}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          {/* The button says what pressing it does. A notice at the top of a dialog
              and a toast afterwards both got missed, and the officer believed a
              real email had gone to a real person. */}
          <button className="btn primary" onClick={send} disabled={!canSend || busy}>
            <Icon name={simulated ? 'science' : channel === 'sms' ? 'sms' : 'send'} size={17} />
            {busy ? 'Sending…' : simulated ? `Log this ${label} — nothing sends` : `Send ${label}`}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {status?.mode === 'simulated' ? (
          <div className="notice warning">
            <Icon name="science" />
            <span>
              Messaging is in <strong>simulated mode</strong>. This will be checked against consent and
              logged on the timeline, but nothing is delivered.
            </span>
          </div>
        ) : null}

        {state && !state.allowed ? (
          <div className="notice danger">
            <Icon name="block" />
            <div style={{ flex: 1 }}>
              <strong>Can't {label} this contact:</strong> {state.reason}
              {/* Recording the opt-in lives on the record, under "Can we reach them?"
                  and on the lead's consent card. A composer is for writing a
                  message; it should not also be where consent gets manufactured. */}
              {missingOptIn ? (
                <div style={{ marginTop: 6 }}>
                  Record their opt-in on the contact card if you hold it.
                </div>
              ) : null}

            </div>
          </div>
        ) : null}

        {channel === 'email' ? (
          <label className="fl">
            <span>Subject</span>
            <input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
        ) : null}
        <label className="fl">
          <span>
            Message <span className="hint">
              {drafting ? '— writing a first draft…' : "— use {first_name} or {lo_first} to personalize"}
            </span>
          </span>
          <textarea
            className="field"
            rows={channel === 'sms' ? 4 : 8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={drafting ? 'Writing a first draft…' : (channel === 'sms' ? 'Hi {first_name}, …' : 'Hi {first_name},\n\n…')}
          />
          {channel === 'sms' ? (
            <span className="hint">
              {body.length} characters · {segments} segment{segments === 1 ? '' : 's'}
            </span>
          ) : null}
        </label>
      </div>
    </Modal>
  )
}
